import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { SELECTOR_PAGE_SIZE } from '../../lib/constants'
import Modal from '../ui/Modal'
import {
  TaskPriorityLabel,
  TaskStatusLabel,
  type OpportunityListResponse,
  type CreateTaskInput,
  type TaskDto,
  type TaskPriorityT,
  type TaskStatusT,
  type UserListItem,
} from '@toga/shared'

interface Props {
  initial: TaskDto | null
  defaultCaseId?: string | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}

export default function TaskFormModal({ initial, defaultCaseId, onClose, onSaved }: Props) {
  const { t } = useTranslation(['tasks', 'common'])
  const isEdit = initial !== null
  const [form, setForm] = useState<CreateTaskInput>({
    opportunityId: initial?.opportunityId ?? defaultCaseId ?? null,
    title: initial?.title ?? '',
    description: initial?.description ?? null,
    status: initial?.status ?? 'PENDIENTE',
    priority: initial?.priority ?? 'MEDIA',
    assignedToId: initial?.assignedToId ?? null,
    dueAt: initial?.dueAt?.slice(0, 10) ?? null,
  })
  const [users, setUsers] = useState<UserListItem[]>([])
  const [cases, setCases] = useState<OpportunityListResponse['items']>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // /users devuelve { users }, no { items } — leer mal daba setUsers(undefined)
    // y reventaba el render (users.map) → ErrorBoundary.
    api
      .get<{ users: UserListItem[] }>('/users')
      .then((r) => setUsers(Array.isArray(r.users) ? r.users : []))
      .catch(() => setUsers([]))
    api
      .get<OpportunityListResponse>(`/opportunities?pageSize=${SELECTOR_PAGE_SIZE}`)
      .then((r) => setCases(Array.isArray(r.items) ? r.items : []))
      .catch(() => setCases([]))
  }, [])

  const upd = useCallback(<K extends keyof CreateTaskInput>(k: K, v: CreateTaskInput[K]) => {
    setForm((f) => ({ ...f, [k]: v }))
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      setError(t('form.errors.titleRequired'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload: CreateTaskInput = {
        ...form,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        opportunityId: form.opportunityId || null,
        assignedToId: form.assignedToId || null,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
      }
      if (isEdit && initial) {
        await api.patch(`/tasks/${initial.id}`, payload)
      } else {
        await api.post('/tasks', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('common:errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title={isEdit ? t('form.titleEdit') : t('form.titleNew')} maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted">{t('form.fields.titleRequired')}</span>
          <input
            value={form.title}
            onChange={(e) => upd('title', e.target.value)}
            className={input}
            required
            autoFocus
          />
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('form.fields.description')}</span>
          <textarea
            value={form.description ?? ''}
            onChange={(e) => upd('description', e.target.value || null)}
            rows={3}
            className={input}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('form.fields.status')}</span>
            <select
              value={form.status}
              onChange={(e) => upd('status', e.target.value as TaskStatusT)}
              className={input}
            >
              {Object.entries(TaskStatusLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.fields.priority')}</span>
            <select
              value={form.priority}
              onChange={(e) => upd('priority', e.target.value as TaskPriorityT)}
              className={input}
            >
              {Object.entries(TaskPriorityLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('form.fields.assignTo')}</span>
            <select
              value={form.assignedToId ?? ''}
              onChange={(e) => upd('assignedToId', e.target.value || null)}
              className={input}
            >
              <option value="">{t('form.options.unassigned')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.fullName}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.fields.dueDate')}</span>
            <input
              type="date"
              value={form.dueAt ?? ''}
              onChange={(e) => upd('dueAt', e.target.value || null)}
              className={input}
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('form.fields.opportunity')}</span>
          <select
            value={form.opportunityId ?? ''}
            onChange={(e) => upd('opportunityId', e.target.value || null)}
            className={input}
            disabled={Boolean(defaultCaseId)}
          >
            <option value="">{t('form.options.noOpportunity')}</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>{c.code} · {c.title}</option>
            ))}
          </select>
        </label>
        {/* Salto directo a la ficha para ACTUAR sobre la tarea (p. ej. evaluar
            el proyecto): navegar cambia de ruta y el modal se desmonta solo. */}
        {form.opportunityId && (
          <Link
            to={`/opportunities/${form.opportunityId}`}
            className="inline-flex items-center gap-1.5 text-xs text-toga-700 dark:text-toga-300 hover:underline"
          >
            <ExternalLink size={12} /> {t('form.openOpportunity')}
          </Link>
        )}

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? t('common:actions.save') : t('form.actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
